import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useArticleContextMenu } from './ArticleContextMenu';
import InlineArticleEditor from './InlineArticleEditor';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HomepageEditModal - Opens article editor modal on homepage
 * Listens to editingArticle from ArticleContextMenuProvider
 */
const HomepageEditModal = () => {
  const { editingArticle, closeEditModal } = useArticleContextMenu();
  const [fullArticle, setFullArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch full article data when editingArticle changes
  useEffect(() => {
    const fetchArticle = async () => {
      if (!editingArticle) {
        setFullArticle(null);
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem('yegoNewsToken');
        // Try to get full article from admin endpoint
        const response = await axios.get(
          `${BACKEND_URL}/api/admin/articles/${editingArticle.id}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setFullArticle(response.data);
      } catch (error) {
        // If admin endpoint fails, use the article data we have
        console.error('Failed to fetch full article:', error);
        setFullArticle(editingArticle);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [editingArticle]);

  const handleSave = (updatedArticle) => {
    // Dispatch event to update article in UI
    window.dispatchEvent(new CustomEvent('articleUpdated', {
      detail: { article: updatedArticle }
    }));
    closeEditModal();
  };

  const handleClose = () => {
    closeEditModal();
  };

  if (!editingArticle) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Modal Container */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Edit Article
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              <span className="ml-3 text-gray-500">Loading article...</span>
            </div>
          ) : fullArticle ? (
            <InlineArticleEditor
              article={fullArticle}
              displaySection={fullArticle.category || 'top_stories'}
              onClose={handleClose}
              onSave={handleSave}
            />
          ) : (
            <div className="flex items-center justify-center py-20">
              <p className="text-gray-500">Failed to load article</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HomepageEditModal;
